"use client";
import { useState, useRef } from "react";
import {
  LogOut,
  ChevronUp,
  CreditCard,
  MessageSquarePlus,
  Receipt,
} from "lucide-react";
import { APP_TAB, AppTab } from "@/components/types";
import { useClarityAuth } from "@/lib/auth/use-clarity-auth";
import { PERMISSION, type Permission } from "@/lib/auth/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import ClarityLogomark from "@/components/ClarityLogomark";
import { Button, type ButtonProps } from "@clarityos/ui/button";
import { FeedbackModal } from "@/features/feedback/containers/FeedbackModal";
import useDisclosure from "@/hooks/useDsiclosure";
import { Tooltip, TooltipContent, TooltipTrigger } from "@clarityos/ui/tooltip";
import { twMerge } from "tailwind-merge";
import ClarityOSLogo from "../../../public/clarityos-logo.svg";
import { Spinner } from "@clarityos/ui";

interface Props {
  activeTab: AppTab;
  onTabChange: (tab: AppTab) => void;
  expanded: boolean;
}

type NavItem = {
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
    size?: number;
  }>;
  tab: AppTab;
  label: string;
  permission: Permission;
};

type NavCategory = {
  category: string;
  items: NavItem[];
};

function getUserDisplayName(user: {
  name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  nickname?: string | null;
  email?: string | null;
}) {
  const givenFamilyName = [user.given_name, user.family_name]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" ");

  return (
    givenFamilyName ||
    user.name?.trim() ||
    user.nickname?.trim() ||
    user.email?.trim() ||
    "Signed in"
  );
}

function getUserInitials(user: {
  name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  nickname?: string | null;
  email?: string | null;
}) {
  const displayName = getUserDisplayName(user);
  const parts = displayName
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return "CU";
}

/* ── Nav categories ── */

const NAV_CATEGORIES: NavCategory[] = [
  {
    category: "Patient Actions",
    items: [
      {
        icon: CreditCard,
        tab: APP_TAB.CARD_COLLECTION,
        label: "Card Collection",
        permission: PERMISSION.CARD_COLLECTION,
      },
    ],
  },
  {
    category: "Practice Management",
    items: [
      {
        icon: Receipt,
        tab: APP_TAB.PATIENT_AR,
        label: "Patient AR",
        permission: PERMISSION.PATIENT_AR,
      },
    ],
  },
];

/* ── Category header ── */
function NavCategoryLabel({
  category,
  expanded,
}: {
  category: string;
  expanded: boolean;
}) {
  if (!expanded) return null;
  return (
    <p className="typo-label-bold-lg text-typography-nav-heading px-3.5 pt-5.5 pb-2.5 uppercase">
      {category}
    </p>
  );
}

/* ── Nav button ── */
function NavButton({
  item,
  isActive,
  onClick,
  expanded,
}: {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  expanded: boolean;
}) {
  const Icon = item.icon;
  const [hovered, setHovered] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({ top: rect.top + rect.height / 2, left: rect.right + 10 });
    }
    setHovered(true);
  };

  return (
    <div
      ref={wrapperRef}
      className={twMerge("relative", expanded ? "w-full" : "w-auto")}
    >
      <Button
        aria-label={item.label}
        startIcon={<Icon strokeWidth={1.5} />}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHovered(false)}
        className={twMerge(
          // The primary variant's transition-colors fades text and background
          // separately, flashing dark-on-dark while toggling active state.
          "transition-none [&_svg]:size-5 [&_svg]:min-w-5",
          expanded
            ? "typo-title-sm h-10 w-full justify-start gap-3 px-3.5"
            : "size-10 gap-0 p-0",
          isActive
            ? "bg-surface-nav-active text-typography-on-surface"
            : "text-typography-primary hover:bg-surface-muted bg-transparent",
        )}
      >
        {expanded && item.label}
      </Button>
      {/* Tooltip on hover when collapsed — fixed position to escape overflow */}
      {!expanded && hovered && (
        <div
          style={{
            position: "fixed",
            left: tooltipPos.left,
            top: tooltipPos.top,
            transform: "translateY(-50%)",
          }}
          className="rounded-12 bg-surface-tertiary typo-body-bold-sm text-typography-on-surface pointer-events-none z-[9999] px-3 py-1.25 whitespace-nowrap shadow-lg"
        >
          {item.label}
        </div>
      )}
    </div>
  );
}

/* ── Sidebar button ── */
function SidebarButton({
  expanded,
  children,
  className,
  ...props
}: ButtonProps & { expanded: boolean }) {
  return (
    <Button
      size="md"
      variant="ghost"
      {...props}
      className={twMerge(
        "text-brand-black transition-[width,padding] duration-200 ease-out [&_svg]:size-5",
        expanded
          ? "w-full justify-start gap-3 px-3.5 py-2.5 whitespace-nowrap"
          : "size-10 justify-center gap-0 p-0",
        className,
      )}
    >
      {expanded && children}
    </Button>
  );
}

/* ── Main sidebar component ── */
export default function IconSidebar({
  activeTab,
  onTabChange,
  expanded,
}: Props) {
  const { logout, user } = useClarityAuth();
  const { isLoading: arePermissionsLoading, hasPermission } = usePermissions();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const feedbackModal = useDisclosure();
  const displayName = getUserDisplayName(user ?? {});
  const displayInitials = getUserInitials(user ?? {});

  const visibleNavCategories = NAV_CATEGORIES.map((navCategory) => ({
    ...navCategory,
    items: navCategory.items.filter((item) => hasPermission(item.permission)),
  })).filter((navCategory) => navCategory.items.length > 0);

  return (
    <aside
      className={twMerge(
        "bg-surface-raised flex h-screen shrink-0 flex-col overflow-x-hidden overflow-y-auto border-r transition-[width] duration-200 ease-out",
        expanded ? "w-[240px] items-stretch" : "w-[72px] items-center",
      )}
    >
      {/* ── Logo area — matches TopBar 64px height ── */}
      <div
        className={twMerge(
          "flex h-16 shrink-0 items-center pt-2 transition-[padding-left] duration-200 ease-out",
          expanded ? "justify-start pl-3" : "justify-center pl-0",
        )}
      >
        <div className="flex h-[26px] items-center">
          {expanded ? (
            <ClarityOSLogo
              aria-label="ClarityOS"
              role="img"
              className="text-content-primary h-full w-auto shrink-0"
            />
          ) : (
            <ClarityLogomark />
          )}
        </div>
      </div>
      {/* ── Nav categories ── */}
      <div
        className={twMerge(
          "flex flex-col transition-[padding] duration-200 ease-out",
          expanded ? "items-stretch px-3" : "items-center px-0 pt-5",
        )}
      >
        {arePermissionsLoading && (
          <Spinner className={expanded ? "mt-5" : undefined} />
        )}
        {visibleNavCategories.map((navCategory) => (
          <div
            key={navCategory.category}
            className={twMerge(
              "flex flex-col gap-1",
              expanded ? "items-stretch" : "items-center",
            )}
          >
            <NavCategoryLabel
              category={navCategory.category}
              expanded={expanded}
            />
            {navCategory.items.map((item) => (
              <NavButton
                key={item.tab}
                item={item}
                isActive={activeTab === item.tab}
                onClick={() => onTabChange(item.tab)}
                expanded={expanded}
              />
            ))}
          </div>
        ))}
      </div>
      {/* Spacer */}
      <div className="flex-1" />
      <div
        className={twMerge(
          "flex py-5 transition-[padding] duration-200 ease-out",
          expanded ? "justify-start px-4" : "justify-center px-0",
        )}
      >
        <SidebarButton
          startIcon={<MessageSquarePlus strokeWidth={2} />}
          onClick={feedbackModal.onOpen}
          expanded={expanded}
          aria-label="Send Feedback"
        >
          Send Feedback
        </SidebarButton>
        {feedbackModal.open && (
          <FeedbackModal
            open
            onClose={feedbackModal.onClose}
            activeTab={activeTab}
          />
        )}
      </div>
      {/* Spacer before settings */}
      {/* ── Settings + Profile ── */}
      <div
        className={twMerge(
          "flex w-full shrink-0 flex-col pb-4 transition-[padding] duration-200 ease-out",
          expanded ? "items-stretch px-3" : "items-center px-0",
        )}
      >
        {/* Divider above profile */}
        <div
          className={twMerge(
            "mb-3 flex w-full justify-center",
            expanded ? "px-1" : "px-4",
          )}
        >
          <div
            className={twMerge(
              "bg-border h-px transition-[width] duration-200 ease-out",
              expanded ? "w-full" : "w-10",
            )}
          />
        </div>

        {/* Profile row */}
        <div className="w-full">
          <Tooltip open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
            <TooltipTrigger asChild>
              <button
                aria-label={`${displayName} account menu`}
                title={displayName}
                className={twMerge(
                  "rounded-12 hover:bg-surface-muted flex cursor-pointer items-center gap-2.5 border-0 transition-colors duration-150",
                  expanded
                    ? "w-full justify-start px-2 py-1.5"
                    : "mx-auto size-9 justify-center p-0",
                  profileMenuOpen ? "bg-surface-muted" : "bg-transparent",
                )}
              >
                <div className="bg-surface-muted flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      className="size-full object-cover"
                    />
                  ) : (
                    <span className="text-typography-secondary typo-body-bold-sm">
                      {displayInitials}
                    </span>
                  )}
                </div>
                {expanded && (
                  <>
                    <span className="typo-body-md min-w-0 flex-1 truncate text-left">
                      {displayName}
                    </span>
                    <ChevronUp
                      className={twMerge(
                        "text-content-tertiary size-3.5 shrink-0 transition-transform duration-200",
                        profileMenuOpen ? "rotate-0" : "rotate-180",
                      )}
                      strokeWidth={2}
                    />
                  </>
                )}
              </button>
            </TooltipTrigger>

            <TooltipContent
              side="top"
              align="start"
              className={twMerge(
                "rounded-8 min-w-45 p-1.5",
                expanded && "w-(--radix-popover-trigger-width)",
              )}
            >
              <div className="mb-1 border-b px-3 py-2.5">
                <p
                  className="text-typography-primary typo-body-bold-md m-0 truncate"
                  title={displayName}
                >
                  {displayName}
                </p>
              </div>
              <button
                onClick={() => {
                  setProfileMenuOpen(false);
                  void logout({
                    logoutParams: {
                      returnTo: window.location.origin,
                    },
                  });
                }}
                className="text-typography-action-red rounded-12 typo-title-sm flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-3 py-2.25 transition-colors duration-100"
              >
                <LogOut className="size-[15px]" strokeWidth={1.8} />
                Log out
              </button>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </aside>
  );
}
